import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { LogoBrain } from '../../components/Logo';
import { 
  Store, Cigarette, Smartphone, Shirt, UtensilsCrossed, 
  Pill, ShoppingCart, Scissors, Wrench, Warehouse, Building,
  Check, ArrowRight, ArrowLeft, Loader2, MapPin, Sparkles, Crown, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Business type icons mapping
const BUSINESS_ICONS = {
  'retail_store': Store,
  'smoke_shop': Cigarette,
  'mobile_electronics': Smartphone,
  'clothing_fashion': Shirt,
  'restaurant_cafe': UtensilsCrossed,
  'pharmacy': Pill,
  'grocery_convenience': ShoppingCart,
  'salon_spa': Scissors,
  'auto_hardware': Wrench,
  'wholesale_distribution': Warehouse,
  'other': Building
};

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup } = useAuth();
  
  // Get pre-selected values from URL params (from landing page)
  const preSelectedBusinessType = searchParams.get('business_type');
  const preSelectedPlan = searchParams.get('plan');
  
  // Multi-step form state - Now 4 steps
  const [step, setStep] = useState(preSelectedBusinessType ? 2 : 1); // Skip to step 2 if business type pre-selected
  const [businessTypes, setBusinessTypes] = useState([]);
  const [usaStates, setUsaStates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState(null);
  const [selectedState, setSelectedState] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(preSelectedPlan || 'free_trial');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    company_name: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');

  // Fetch business types on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        
        // Fetch business types
        const btRes = await fetch(`${API_URL}/api/business-types/`);
        const btData = await btRes.json();
        setBusinessTypes(btData || []);
        
        // If business type was pre-selected from landing page, set it
        if (preSelectedBusinessType && btData) {
          const preSelected = btData.find(bt => bt.id === preSelectedBusinessType);
          if (preSelected) {
            setSelectedBusinessType(preSelected);
          }
        }
        
        // Fetch USA states
        const statesRes = await fetch(`${API_URL}/api/business-types/tax/states`);
        const statesData = await statesRes.json();
        setUsaStates(statesData.states || []);
        
        // Fetch subscription plans
        const plansRes = await fetch(`${API_URL}/api/subscriptions/plans`);
        const plansData = await plansRes.json();
        setPlans(plansData.plans || []);
        
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [preSelectedBusinessType]);

  const handleBusinessTypeSelect = (bt) => {
    setSelectedBusinessType(bt);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const success = await signup({
        email: formData.email,
        password: formData.password,
        full_name: `${formData.first_name} ${formData.last_name}`,
        company_name: formData.company_name || "My Business",
        business_type: selectedBusinessType?.id || 'retail_store',
        state_code: selectedState || null,
        selected_plan: selectedPlan,
        currency: 'usd'
      });
      
      if (success) {
        navigate('/dashboard');
      } else {
        setError('Signup failed. Email may already be registered.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && !selectedBusinessType) {
      setError('Please select your business type');
      return;
    }
    setError('');
    setStep(step + 1);
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
  };

  const getIcon = (iconName) => {
    const IconComponent = BUSINESS_ICONS[iconName] || Store;
    return IconComponent;
  };

  // Loading state
  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <LogoBrain size={40} animate={true} />
          <span className="text-white font-bold text-xl">FlowFeed ERP</span>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto w-full px-6 mb-8">
        <div className="flex items-center justify-center gap-2 sm:gap-4">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${step >= s ? 'text-white' : 'text-white/40'}`}>
                <div className={`w-8 sm:w-10 h-8 sm:h-10 rounded-full flex items-center justify-center font-semibold transition-all text-sm ${
                  step > s ? 'bg-green-500' : step === s ? 'bg-blue-500' : 'bg-white/10'
                }`}>
                  {step > s ? <Check className="h-4 w-4" /> : s}
                </div>
                <span className="hidden md:block text-sm font-medium">
                  {s === 1 ? 'Business' : s === 2 ? 'Location' : s === 3 ? 'Plan' : 'Account'}
                </span>
              </div>
              {s < 4 && <div className={`w-8 sm:w-16 h-1 rounded ${step > s ? 'bg-green-500' : 'bg-white/10'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-start justify-center px-6 pb-12">
        <AnimatePresence mode="wait">
          {/* Step 1: Business Type Selection */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="w-full max-w-5xl"
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                  What type of business do you run?
                </h1>
                <p className="text-white/60 text-lg">
                  We'll customize FlowFeed ERP for your specific industry
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                {businessTypes.map((bt) => {
                  const IconComponent = getIcon(bt.id);
                  const isSelected = selectedBusinessType?.id === bt.id;
                  
                  return (
                    <motion.div
                      key={bt.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleBusinessTypeSelect(bt)}
                      className={`relative p-6 rounded-2xl cursor-pointer transition-all border-2 ${
                        isSelected 
                          ? 'bg-white/10 border-blue-500 shadow-lg shadow-blue-500/20' 
                          : 'bg-white/5 border-transparent hover:bg-white/10 hover:border-white/20'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-3 right-3 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                        style={{ backgroundColor: `${bt.color}20` }}
                      >
                        <IconComponent className="h-7 w-7" style={{ color: bt.color }} />
                      </div>
                      <h3 className="text-white font-semibold mb-1">{bt.name}</h3>
                      <p className="text-white/50 text-sm line-clamp-2">{bt.description}</p>
                    </motion.div>
                  );
                })}
              </div>

              {error && (
                <p className="text-red-400 text-center mb-4">{error}</p>
              )}

              <div className="flex justify-center">
                <Button
                  onClick={nextStep}
                  disabled={!selectedBusinessType}
                  className="px-8 py-6 text-lg bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90"
                  data-testid="next-step-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Location Selection */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="w-full max-w-2xl"
            >
              <div className="text-center mb-8">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${selectedBusinessType?.color}20` }}
                >
                  {(() => {
                    const IconComponent = getIcon(selectedBusinessType?.id);
                    return <IconComponent className="h-8 w-8" style={{ color: selectedBusinessType?.color }} />;
                  })()}
                </div>
                <h1 className="text-3xl font-bold text-white mb-3">
                  Where is your {selectedBusinessType?.name} located?
                </h1>
                <p className="text-white/60">
                  Select your state for accurate tax calculations
                </p>
              </div>

              <div className="bg-white/5 rounded-2xl p-6 border border-white/10 mb-8">
                <label className="block text-white/80 text-sm font-medium mb-3">
                  <MapPin className="h-4 w-4 inline mr-2" />
                  Select Your State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="state-select"
                >
                  <option value="" className="bg-slate-800">-- Select State --</option>
                  {usaStates.map((state) => (
                    <option key={state.code} value={state.code} className="bg-slate-800">
                      {state.name} ({state.tax_rate}% state tax)
                    </option>
                  ))}
                </select>
                
                {selectedState && (
                  <div className="mt-4 p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
                    <p className="text-blue-300 text-sm">
                      <Check className="h-4 w-4 inline mr-2" />
                      Sales tax will be automatically calculated at checkout
                    </p>
                  </div>
                )}
              </div>

              {/* Business Type Features Preview */}
              {selectedBusinessType && (
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10 mb-8">
                  <h3 className="text-white font-semibold mb-4">
                    Features for {selectedBusinessType.name}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['POS Terminal', 'Inventory Management', 'Sales Reports', 'Customer Management', 'Tax Compliance', 'Multi-Payment'].map((feature) => (
                      <div key={feature} className="flex items-center gap-2 text-white/70 text-sm">
                        <Check className="h-4 w-4 text-green-400" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button
                  onClick={prevStep}
                  variant="outline"
                  className="px-6 py-5 border-white/20 text-white hover:bg-white/10"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Back
                </Button>
                <Button
                  onClick={nextStep}
                  className="px-8 py-5 bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90"
                  data-testid="next-step-2-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Plan Selection */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="w-full max-w-5xl"
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-3">
                  Choose your plan
                </h1>
                <p className="text-white/60">
                  Start with a 7-day free trial • Upgrade anytime
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {plans.map((plan) => {
                  const isSelected = selectedPlan === plan.id;
                  const isPopular = plan.popular;
                  
                  return (
                    <motion.div
                      key={plan.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`relative p-6 rounded-2xl cursor-pointer transition-all border-2 ${
                        isSelected 
                          ? 'bg-white/10 border-blue-500 shadow-lg shadow-blue-500/20' 
                          : 'bg-white/5 border-transparent hover:bg-white/10 hover:border-white/20'
                      }`}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-xs font-bold text-white flex items-center gap-1">
                          <Sparkles className="h-3 w-3" /> MOST POPULAR
                        </div>
                      )}
                      
                      {isSelected && (
                        <div className="absolute top-3 right-3 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                      
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          {plan.id === 'enterprise' ? (
                            <Crown className="h-5 w-5 text-amber-400" />
                          ) : plan.id === 'business' ? (
                            <Zap className="h-5 w-5 text-blue-400" />
                          ) : null}
                          <h3 className="text-white font-bold text-lg">{plan.name}</h3>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-white">
                            ${plan.price}
                          </span>
                          {plan.price > 0 && (
                            <span className="text-white/50 text-sm">/month</span>
                          )}
                        </div>
                        {plan.id === 'free_trial' && (
                          <p className="text-emerald-400 text-sm mt-1">7 days free</p>
                        )}
                      </div>
                      
                      <p className="text-white/60 text-sm mb-4">{plan.description}</p>
                      
                      <ul className="space-y-2">
                        {plan.features.slice(0, 5).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-white/70 text-sm">
                            <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                        {plan.features.length > 5 && (
                          <li className="text-white/50 text-sm pl-6">
                            +{plan.features.length - 5} more features
                          </li>
                        )}
                      </ul>
                    </motion.div>
                  );
                })}
              </div>

              <div className="flex justify-between">
                <Button
                  onClick={prevStep}
                  variant="outline"
                  className="px-6 py-5 border-white/20 text-white hover:bg-white/10"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Back
                </Button>
                <Button
                  onClick={nextStep}
                  className="px-8 py-5 bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90"
                  data-testid="next-step-3-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Account Details */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="w-full max-w-lg"
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-3">
                  Create your account
                </h1>
                <p className="text-white/60">
                  {selectedPlan === 'free_trial' 
                    ? 'Start your 7-day free trial • No credit card required'
                    : `Starting with ${plans.find(p => p.id === selectedPlan)?.name} plan`
                  }
                </p>
              </div>

              <form onSubmit={handleSignup} className="space-y-5">
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                  {/* Selected Business Type & Plan Badge */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${selectedBusinessType?.color}20` }}
                      >
                        {(() => {
                          const IconComponent = getIcon(selectedBusinessType?.id);
                          return <IconComponent className="h-5 w-5" style={{ color: selectedBusinessType?.color }} />;
                        })()}
                      </div>
                      <div>
                        <p className="text-white font-medium">{selectedBusinessType?.name}</p>
                        <p className="text-white/50 text-sm">
                          {selectedState ? usaStates.find(s => s.code === selectedState)?.name : 'USA'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">{plans.find(p => p.id === selectedPlan)?.name}</p>
                      <p className="text-emerald-400 text-sm">
                        {selectedPlan === 'free_trial' ? '7 days free' : `$${plans.find(p => p.id === selectedPlan)?.price}/mo`}
                      </p>
                    </div>
                  </div>

                  {/* Business Name */}
                  <div className="mb-4">
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Business Name *
                    </label>
                    <Input
                      type="text"
                      placeholder="Your Business Name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      required
                      data-testid="company-name-input"
                    />
                  </div>

                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        First Name *
                      </label>
                      <Input
                        type="text"
                        placeholder="John"
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        required
                        data-testid="first-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Last Name *
                      </label>
                      <Input
                        type="text"
                        placeholder="Doe"
                        value={formData.last_name}
                        onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        required
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="mb-4">
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      placeholder="john@mybusiness.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      required
                      data-testid="email-input"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Password *
                    </label>
                    <Input
                      type="password"
                      placeholder="Create a strong password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      required
                      minLength={8}
                      data-testid="password-input"
                    />
                    <p className="text-white/40 text-xs mt-1">Minimum 8 characters</p>
                  </div>
                </div>

                {error && (
                  <p className="text-red-400 text-center">{error}</p>
                )}

                <div className="flex justify-between pt-2">
                  <Button
                    type="button"
                    onClick={prevStep}
                    variant="outline"
                    className="px-6 py-5 border-white/20 text-white hover:bg-white/10"
                  >
                    <ArrowLeft className="mr-2 h-5 w-5" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-5 bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90"
                    data-testid="signup-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        Start Free Trial
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-center text-white/50 text-sm">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-blue-400 hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Signup;
